// CB:BEGIN help-articles
/**
 * In-app help articles indexed by slug.
 * Referenced by NextAction.helpArticles[] and rendered by HelpPanel.
 */

export interface HelpArticle {
  slug: string;
  title: string;
  summary: string;
  body: string; // Markdown-ish plain text for now
}

export const HELP_ARTICLES: Record<string, HelpArticle> = {
  'escrow-and-payouts': {
    slug: 'escrow-and-payouts',
    title: 'How Escrow & Payouts Work',
    summary: 'Your funds are held securely until work is approved.',
    body: `
## How CoreBids Escrow Works

When you accept a contractor bid, your payment is held in a secure escrow account—not released to the contractor until work is approved.

**Step-by-step:**
1. You fund the escrow after signing the project contract.
2. The contractor begins work.
3. At each milestone, the contractor submits photos/evidence.
4. You review and approve each milestone.
5. CoreBids releases the payment for that milestone to the contractor.
6. Final payment is released after you confirm project completion.

**Why escrow?**
It protects both parties: contractors know they'll be paid for completed work, and homeowners know funds are only released after approval.

**Questions?** Email billing@corebids.com
    `.trim(),
  },

  'after-selecting-a-contractor': {
    slug: 'after-selecting-a-contractor',
    title: 'After You Select a Contractor',
    summary: 'What happens next after choosing your contractor.',
    body: `
## Next Steps After Contractor Selection

Once you've chosen a contractor from the bids, here's what happens:

1. **Sign the project contract** – Review and e-sign the contract within the platform.
2. **Fund escrow** – Deposit the project amount into the secure escrow account.
3. **Choose your roof color** – Select from the contractor's approved material palette.
4. **Set a start date** – Confirm when work should begin.
5. **Work begins** – The contractor starts on the agreed date.
6. **Milestone approvals** – Approve completed milestones to release payment.
7. **Project complete** – Final payment released after your approval.

**Tip:** Complete each step quickly to avoid delays — the contractor's schedule may fill up.

**Questions?** Email support@corebids.com
    `.trim(),
  },

  'roof-color-selection': {
    slug: 'roof-color-selection',
    title: 'Choosing Your Roof Color',
    summary: 'How to select your preferred roof color from the approved palette.',
    body: `
## Selecting Your Roof Color

Your contractor provides an approved list of materials and colors from certified manufacturers.

**To select your color:**
1. Navigate to your project page.
2. Scroll to the "Roof Color" section.
3. Choose from the available options (manufacturer, product line, color).
4. Save your selection — the contractor will use this to order materials.

**Important:**
- You must select a color before work can begin.
- Once materials are ordered, color changes may incur additional costs.
- If you have questions about a specific color, contact your contractor directly via the project messaging.

**Questions?** Email support@corebids.com
    `.trim(),
  },

  'disputes-and-timelines': {
    slug: 'disputes-and-timelines',
    title: 'Disputes & Project Timelines',
    summary: 'What to do if something goes wrong with your project.',
    body: `
## Handling Disputes

We want every project to go smoothly. If you have concerns:

**Before opening a dispute:**
- Use the project message thread to communicate with your contractor.
- Give the contractor a reasonable chance to address the issue.

**Opening a dispute:**
- Contact support@corebids.com with your project ID and description.
- CoreBids will review evidence from both parties.
- Funds remain held in escrow during the dispute — no money leaves until resolved.

**Timeline expectations:**
- Most disputes are reviewed within 3–5 business days.
- Complex disputes may take up to 14 business days.
- You will receive email updates at each stage.

**Project timeline delays:**
- If work is delayed, contact your contractor first.
- If unresponsive for 48+ hours, email support@corebids.com.

**Questions?** Email support@corebids.com
    `.trim(),
  },

  'billing-vs-support': {
    slug: 'billing-vs-support',
    title: 'Billing vs. Support: Who to Contact',
    summary: 'Understanding which team handles your question.',
    body: `
## Who Should You Contact?

**Support (support@corebids.com)**
- Project status questions
- Contractor communication issues
- Platform how-to questions
- Disputes and timeline concerns
- Account access problems

**Billing (billing@corebids.com)**
- Payment receipts and invoices
- Escrow funding issues
- Refund requests
- Payment method changes
- Payout questions (for contractors)

**Response times:**
- Support: typically within 1 business day
- Billing: typically within 1–2 business days

We're here to help. Don't hesitate to reach out!
    `.trim(),
  },
};

export function getArticle(slug: string): HelpArticle | null {
  return HELP_ARTICLES[slug] ?? null;
}

export function getArticles(slugs: string[]): HelpArticle[] {
  return slugs.map((s) => HELP_ARTICLES[s]).filter(Boolean) as HelpArticle[];
}
// CB:END help-articles
